
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Building, Users, Calendar, DollarSign, Plus, Edit3, Trash2, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MicroInternship {
  id: string;
  title: string;
  description: string;
  duration: string;
  compensation: string;
  skills: string[];
  deadline: string;
  applicants: number;
  status: 'active' | 'closed' | 'draft';
  type: 'remote' | 'hybrid' | 'onsite';
}

const MicroInternshipProgram: React.FC = () => {
  const { toast } = useToast();
  const [internships, setInternships] = useState<MicroInternship[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    compensation: '',
    skills: '',
    deadline: '',
    type: 'remote' as 'remote' | 'hybrid' | 'onsite'
  });

  const createInternship = () => {
    if (!formData.title || !formData.description) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const newInternship: MicroInternship = {
      id: Date.now().toString(),
      title: formData.title,
      description: formData.description,
      duration: formData.duration,
      compensation: formData.compensation,
      skills: formData.skills.split(',').map(s => s.trim()),
      deadline: formData.deadline,
      applicants: 0,
      status: 'active',
      type: formData.type
    };

    setInternships([newInternship, ...internships]);
    setFormData({
      title: '', description: '', duration: '', compensation: '', 
      skills: '', deadline: '', type: 'remote'
    });
    setShowCreateForm(false);

    toast({
      title: "Micro-Internship Created!",
      description: "Your micro-internship opportunity has been posted successfully."
    });
  };

  const deleteInternship = (id: string) => {
    setInternships(internships.filter(i => i.id !== id));
    toast({
      title: "Internship Deleted",
      description: "The micro-internship has been removed."
    });
  };

  const toggleStatus = (id: string) => {
    setInternships(internships.map(i => 
      i.id === id 
        ? { ...i, status: i.status === 'active' ? 'closed' : 'active' as const }
        : i
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'remote': return 'bg-blue-100 text-blue-800';
      case 'hybrid': return 'bg-purple-100 text-purple-800';
      case 'onsite': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-slate-500" />
            Host Micro-Internship Program
          </CardTitle>
          <CardDescription>
            Create and manage short-term internship opportunities for talented teens.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Program Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-4 text-center">
                <Building className="h-6 w-6 mx-auto mb-2 text-slate-600" />
                <div className="text-2xl font-bold text-slate-700">{internships.length}</div>
                <p className="text-sm text-slate-600">Total Programs</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold text-green-700">
                  {internships.filter(i => i.status === 'active').length}
                </div>
                <p className="text-sm text-green-600">Active Programs</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 text-center">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold text-blue-700">
                  {internships.reduce((sum, i) => sum + i.applicants, 0)}
                </div>
                <p className="text-sm text-blue-600">Total Applicants</p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold text-purple-700">
                  {internships.filter(i => i.compensation !== 'Unpaid').length}
                </div>
                <p className="text-sm text-purple-600">Paid Programs</p>
              </CardContent>
            </Card>
          </div>

          {/* Create New Internship */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Your Micro-Internship Programs</h3>
              <Button 
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="bg-slate-500 hover:bg-slate-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Program
              </Button>
            </div>

            {showCreateForm && (
              <Card className="mb-6 border-slate-200">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-4">Create New Micro-Internship</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Program Title *</label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="e.g., Social Media Marketing Intern"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Duration</label>
                      <Input
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        placeholder="e.g., 4 weeks, 2 months"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Compensation</label>
                      <Input
                        value={formData.compensation}
                        onChange={(e) => setFormData({...formData, compensation: e.target.value})}
                        placeholder="e.g., $500, Unpaid, Course Credit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="remote">Remote</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="onsite">On-site</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Application Deadline</label>
                      <Input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Required Skills</label>
                      <Input
                        value={formData.skills}
                        onChange={(e) => setFormData({...formData, skills: e.target.value})}
                        placeholder="Communication, Design, Research (comma separated)"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Describe the internship role, responsibilities, and what students will learn..."
                      rows={4}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={createInternship} className="bg-slate-500 hover:bg-slate-600">
                      Create Program
                    </Button>
                    <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Internships List */}
          <div className="space-y-4">
            {internships.length === 0 ? (
              <div className="text-center py-8">
                <Building className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                <h3 className="text-lg font-medium mb-2">No Micro-Internships Created Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first micro-internship program to start attracting talented teen applicants.
                </p>
                <Button 
                  onClick={() => setShowCreateForm(true)}
                  className="bg-slate-500 hover:bg-slate-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Program
                </Button>
              </div>
            ) : (
              internships.map((internship) => (
                <Card key={internship.id} className="border-l-4 border-l-slate-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{internship.title}</h3>
                        <div className="flex gap-2 mt-1">
                          <Badge className={getStatusColor(internship.status)}>
                            {internship.status}
                          </Badge>
                          <Badge className={getTypeColor(internship.type)}>
                            {internship.type}
                          </Badge>
                        </div>
                      </div>
                      <Badge variant="outline">
                        {internship.applicants} applicants
                      </Badge>
                    </div>

                    <p className="text-muted-foreground mb-4">{internship.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{internship.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{internship.compensation}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{internship.applicants} applied</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Due: {internship.deadline}</span>
                      </div>
                    </div>

                    {internship.skills.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Required Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {internship.skills.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => toggleStatus(internship.id)}
                      >
                        {internship.status === 'active' ? 'Close' : 'Reopen'} Program
                      </Button>
                      <Button variant="outline">
                        <Eye className="h-4 w-4 mr-2" />
                        View Applicants
                      </Button>
                      <Button variant="outline">
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => deleteInternship(internship.id)}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MicroInternshipProgram;
